import { db } from '../components/Dexie';
import { log } from './loggingService';

/**
 * Synchronizes products with the remote API.
 * @param {boolean} force - If true, clears local data and forces a full sync.
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const syncTechyShopProducts = async (force = false) => {
  try {
    const now = new Date();

    if (!force) {
      // Check if sync happened in the last 24 hours
      const lastSync = await db.genericTable.get({ table: 'sync_info', field: 'techyshop_prodotti' });
      if (lastSync && lastSync.date) {
        const lastSyncDate = new Date(lastSync.date);
        const diffInMs = now - lastSyncDate;
        const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

        if (diffInDays < 1) {
          log("Sincronizzazione prodotti non necessaria (ultima < 24h).", 'INFO');
          return { success: true, message: "Dati aggiornati (cache)." };
        }
      }
    }

    log("Sincronizzazione prodotti in corso...", 'INFO');

    const response = await fetch('https://deadapi.techysumo.it/techyshop/searchProdotto', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': process.env.REACT_APP_API_KEY,
      },
      body: JSON.stringify({})
    });

    const result = await response.json();

    if (result.result && Array.isArray(result.details)) {
      if (force || result.details.length > 0) {
        await db.techyshop_prodotti.clear();
      }

      const normalizedProducts = result.details.map(p => ({
        ...p,
        id: parseInt(p.id),
        prezzo: parseFloat(p.prezzo) || 0,
        prezzo_scontato: parseFloat(p.prezzo_scontato) || 0,
        quantita: parseInt(p.quantita) || 0,
        visibile: (p.visibile === 1 || p.visibile === true || String(p.visibile).toLowerCase() === 'true') ? 1 : 0,
        in_evidenza: (p.in_evidenza === 1 || p.in_evidenza === true || String(p.in_evidenza).toLowerCase() === 'true') ? 1 : 0,
        in_sconto: (p.in_sconto === 1 || p.in_sconto === true || String(p.in_sconto).toLowerCase() === 'true') ? 1 : 0,
      }));

      await db.techyshop_prodotti.bulkPut(normalizedProducts);

      // Update last sync date
      await db.genericTable.put({
        table: 'sync_info',
        field: 'techyshop_prodotti',
        value: 'last_sync',
        date: now.toISOString()
      });

      log(`Sincronizzati ${normalizedProducts.length} prodotti.`, 'INFO');
      return { success: true, message: `Sincronizzati ${normalizedProducts.length} prodotti con successo!` };
    } else {
      throw new Error(result.text || "Errore durante il download dei prodotti.");
    }
  } catch (error) {
    log("Errore durante la sincronizzazione dei prodotti:", 'ERROR', error);
    return { success: false, message: `Errore: ${error.message}` };
  }
};

export const syncProducts = syncTechyShopProducts;

/**
 * Sincronizza gli attributi e i loro valori dal server.
 */
export const syncAttributes = async () => {
  try {
    log("Sincronizzazione attributi in corso...", 'INFO');
    
    // 1. Scarica gli attributi
    const responseAttr = await fetch('https://deadapi.techysumo.it/techyshop/searchAttributo', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': process.env.REACT_APP_API_KEY,
      },
      body: JSON.stringify({})
    });
    const resultAttr = await responseAttr.json();
    
    // 2. Scarica i valori degli attributi
    const responseVal = await fetch('https://deadapi.techysumo.it/techyshop/searchAttributoValore', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': process.env.REACT_APP_API_KEY,
      },
      body: JSON.stringify({})
    });
    const resultVal = await responseVal.json();

    if (resultAttr.result && Array.isArray(resultAttr.details)) {
      await db.techyshop_attributi.clear();
      await db.techyshop_attributo_valori.clear();
      
      const attributi = resultAttr.details.map(attr => ({ 
        ...attr, 
        id: parseInt(attr.id) 
      }));
      
      await db.techyshop_attributi.bulkPut(attributi);

      if (resultVal.result && Array.isArray(resultVal.details)) {
        const valori = resultVal.details.map(v => ({
          ...v,
          id: parseInt(v.id),
          attributo_id: parseInt(v.attributo_id)
        }));
        await db.techyshop_attributo_valori.bulkPut(valori);
      }
      
      return { success: true, message: `Sincronizzati ${attributi.length} attributi con successo!` };
    }
    throw new Error(resultAttr.text || "Errore durante il download degli attributi.");
  } catch (error) {
    log("Errore syncAttributes:", 'ERROR', error);
    return { success: false, message: error.message };
  }
};

/**
 * Sincronizza le associazioni attributi dei prodotti.
 */
export const syncProductAttributes = async () => {
  try {
    log("Sincronizzazione associazioni attributi in corso...", 'INFO');
    const response = await fetch('https://deadapi.techysumo.it/techyshop/listProdottoAttributi', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': process.env.REACT_APP_API_KEY,
      },
      body: JSON.stringify({})
    });
    const result = await response.json();
    if (result.result && Array.isArray(result.details)) {
      await db.techyshop_prodotto_attributo.clear();
      await db.techyshop_prodotto_attributo.bulkPut(result.details.map(pa => ({
        ...pa,
        id: parseInt(pa.id),
        prodotto_id: parseInt(pa.prodotto_id),
        prodotto_variante_id: parseInt(pa.prodotto_variante_id || 0),
        attributo_id: parseInt(pa.attributo_id),
        attributo_valore_id: parseInt(pa.valore_id)
      })));
      return { success: true, message: "Associazioni attributi sincronizzate!" };
    }
    throw new Error(result.text || "Errore syncProductAttributes");
  } catch (error) {
    log("Errore syncProductAttributes:", 'ERROR', error);
    return { success: false, message: error.message };
  }
};

/**
 * Sincronizza le categorie.
 */
export const syncCategories = async () => {
  try {
    log("Sincronizzazione categorie in corso...", 'INFO');
    const response = await fetch('https://deadapi.techysumo.it/techyshop/searchCategoria', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': process.env.REACT_APP_API_KEY,
      },
      body: JSON.stringify({})
    });
    const result = await response.json();
    if (result.result && Array.isArray(result.details)) {
      await db.techyshop_categorie.clear();
      const normalized = result.details.map(c => ({
        ...c,
        id: parseInt(c.id),
        categoria_padre_id: parseInt(c.categoria_padre_id || c.parent_id || 0)
      }));
      await db.techyshop_categorie.bulkPut(normalized);
      log(`Sincronizzate ${normalized.length} categorie.`, 'INFO');
      return { success: true, message: "Categorie sincronizzate!" };
    }
    throw new Error(result.text || "Errore syncCategories");
  } catch (error) {
    log("Errore syncCategories:", 'ERROR', error);
    return { success: false, message: error.message };
  }
};

/**
 * Sincronizza i media.
 */
export const syncMedia = async () => {
  try {
    log("Sincronizzazione media in corso...", 'INFO');
    const response = await fetch('https://deadapi.techysumo.it/techyshop/searchMedia', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': process.env.REACT_APP_API_KEY,
      },
      body: JSON.stringify({})
    });
    const result = await response.json();
    if (result.result && Array.isArray(result.details)) {
      await db.techyshop_medias.clear();
      await db.techyshop_medias.bulkPut(result.details.map(m => ({
        ...m,
        id: parseInt(m.id),
        element_id: parseInt(m.element_id),
        ordinamento: parseInt(m.ordinamento) || 0,
        principale: (m.principale === 1 || m.principale === true) ? 1 : 0
      })));
      return { success: true, message: "Media sincronizzati!" };
    }
    throw new Error(result.text || "Errore syncMedia");
  } catch (error) {
    log("Errore syncMedia:", 'ERROR', error);
    return { success: false, message: error.message };
  }
};

/**
 * Aggiunge un media.
 */
export const addMedia = async (file, metadata) => {
  try {
    log("Caricamento nuovo media...", 'INFO');
    
    const formData = new FormData();
    formData.append('immagine', file);
    
    // Append all metadata fields except filePreview
    Object.entries(metadata).forEach(([key, value]) => {
      if (key !== 'filePreview' && value !== null && value !== undefined) {
        formData.append(key, value);
      }
    });

    const response = await fetch('https://deadapi.techysumo.it/techyshop/addMedia', {
      method: 'POST',
      headers: {
        'Authorization': process.env.REACT_APP_API_KEY,
      },
      body: formData // Fetch handles FormData automatically
    });
    const result = await response.json();
    if (result.result) {
      // Typically we'd sync after upload
      await syncMedia();
      return { success: true, message: "Media aggiunto con successo!" };
    }
    throw new Error(result.text || "Errore addMedia");
  } catch (error) {
    log("Errore addMedia:", 'ERROR', error);
    return { success: false, message: error.message };
  }
};

/**
 * Elimina un media.
 */
export const deleteMedia = async (id) => {
  try {
    log(`Eliminazione media ID ${id}...`, 'INFO');
    const response = await fetch('https://deadapi.techysumo.it/techyshop/delMedia', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': process.env.REACT_APP_API_KEY,
      },
      body: JSON.stringify({ id })
    });
    const result = await response.json();
    if (result.result) {
      await db.techyshop_medias.delete(id);
      return { success: true, message: "Media eliminato!" };
    }
    throw new Error(result.text || "Errore deleteMedia");
  } catch (error) {
    log("Errore deleteMedia:", 'ERROR', error);
    return { success: false, message: error.message };
  }
};

/**
 * Crea una nuova categoria nel sistema.
 */
export const addCategory = async (nome) => {
  try {
    const slug = nome.toLowerCase().trim().replace(/\s+/g, '-');
    const response = await fetch('https://deadapi.techysumo.it/techyshop/addCategoria', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': process.env.REACT_APP_API_KEY,
      },
      body: JSON.stringify({ nome, slug })
    });
    const result = await response.json();
    if (result.result) {
      const newId = result.details.id || result.details;
      const newCategory = { id: parseInt(newId), nome, categoria_padre_id: 0, slug };
      await db.techyshop_categorie.put(newCategory);
      return { success: true, category: newCategory };
    }
    throw new Error(result.text || "Errore addCategory");
  } catch (error) {
    log("Errore addCategory:", 'ERROR', error);
    return { success: false, message: error.message };
  }
};

/**
 * Recupera le categorie associate a un prodotto specifico.
 */
export const fetchProductCategories = async (prodotto_id) => {
  try {
    const response = await fetch('https://deadapi.techysumo.it/techyshop/searchProdottoCategoria', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': process.env.REACT_APP_API_KEY,
      },
      body: JSON.stringify({ prodotto_id })
    });
    const result = await response.json();
    if (result.result && Array.isArray(result.details)) {
      // Update local association table for this product
      const pId = parseInt(prodotto_id);
      await db.techyshop_prodotto_categoria.where('prodotto_id').equals(pId).delete();
      const associations = result.details.map(c => ({
        prodotto_id: pId,
        categoria_id: parseInt(c.categoria_id || c.id)
      }));
      await db.techyshop_prodotto_categoria.bulkPut(associations);
      return { success: true, details: associations };
    }
    throw new Error(result.text || "Errore fetchProductCategories");
  } catch (error) {
    log("Errore fetchProductCategories:", 'ERROR', error);
    return { success: false, message: error.message };
  }
};

/**
 * Associa una categoria a un prodotto.
 */
export const addProductCategory = async (prodotto_id, categoria_id) => {
  try {
    const response = await fetch('https://deadapi.techysumo.it/techyshop/addProdottoCategoria', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': process.env.REACT_APP_API_KEY,
      },
      body: JSON.stringify({ prodotto_id, categoria_id })
    });
    const result = await response.json();
    if (result.result) {
      await db.techyshop_prodotto_categoria.put({
        prodotto_id: parseInt(prodotto_id),
        categoria_id: parseInt(categoria_id)
      });
      return { success: true, message: "Categoria aggiunta!" };
    }
    throw new Error(result.text || "Errore addProductCategory");
  } catch (error) {
    log("Errore addProductCategory:", 'ERROR', error);
    return { success: false, message: error.message };
  }
};

/**
 * Sincronizza le varianti di un prodotto specifico.
 */
export const syncProductVariants = async (prodotto_id, force = false) => {
  try {
    const now = new Date();

    if (!force) {
      // Check last sync for this specific product variants
      const lastSync = await db.genericTable.get({
        table: 'sync_info_product_variants',
        field: prodotto_id.toString()
      });

      if (lastSync && lastSync.date) {
        const lastSyncDate = new Date(lastSync.date);
        if ((now - lastSyncDate) / (1000 * 60 * 60 * 24) < 1) {
          log(`Varianti prodotto ${prodotto_id} aggiornate (cache).`, 'INFO');
          return { success: true, message: "Dati aggiornati (cache)." };
        }
      }
    }

    log(`Sincronizzazione varianti prodotto ${prodotto_id} in corso...`, 'INFO');

    const response = await fetch('https://deadapi.techysumo.it/techyshop/searchProdottoVariante', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': process.env.REACT_APP_API_KEY,
      },
      body: JSON.stringify({ prodotto_id })
    });

    const result = await response.json();

    if (result.result && Array.isArray(result.details)) {
      // Clear old variants for this product
      await db.techyshop_prodotto_varianti.where('prodotto_id').equals(parseInt(prodotto_id)).delete();
      await db.techyshop_prodotto_attributo.where('prodotto_id').equals(parseInt(prodotto_id)).delete();

      const normalized = result.details.map(v => ({
        ...v,
        id: parseInt(v.id),
        prodotto_id: parseInt(prodotto_id),
        prezzo: parseFloat(v.prezzo) || 0,
        prezzo_scontato: parseFloat(v.prezzo_scontato) || 0,
        quantita: parseInt(v.quantita) || 0,
        visibile: (v.visibile === 1 || v.visibile === true) ? 1 : 0,
        default: (v.default === 1 || v.default === true) ? 1 : 0,
        created_at: v.created_at || now.toISOString()
      }));

      await db.techyshop_prodotto_varianti.bulkPut(normalized);

      // Sync attributes associations for these variants
      await syncProductVariantAttributes(prodotto_id);

      // Update last sync date
      await db.genericTable.put({
        table: 'sync_info_product_variants',
        field: prodotto_id.toString(),
        value: 'last_sync',
        date: now.toISOString()
      });

      return { success: true, message: "Varianti prodotto sincronizzate!", details: normalized };
    }
    throw new Error(result.text || "Errore searchProdottoVariante");
  } catch (error) {
    log("Errore syncProductVariants:", 'ERROR', error);
    return { success: false, message: error.message };
  }
};

/**
 * Sincronizza le associazioni attributi delle varianti di un prodotto.
 */
export const syncProductVariantAttributes = async (prodotto_id) => {
  try {
    log(`Sincronizzazione attributi varianti prodotto ${prodotto_id}...`, 'INFO');
    const response = await fetch('https://deadapi.techysumo.it/techyshop/searchProdottoAttributo', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': process.env.REACT_APP_API_KEY,
      },
      body: JSON.stringify({ prodotto_id })
    });
    
    const result = await response.json();
    
    if (result.result && Array.isArray(result.details)) {
      // Clear old associations for this product variants
      await db.techyshop_prodotto_attributo.where('prodotto_id').equals(parseInt(prodotto_id)).delete();
      
      const normalized = result.details.map(pa => ({
        id: parseInt(pa.id),
        prodotto_id: parseInt(pa.prodotto_id),
        prodotto_variante_id: parseInt(pa.prodotto_variante_id || 0),
        attributo_id: parseInt(pa.attributo_id),
        attributo_valore_id: parseInt(pa.attributo_valore_id || pa.valore_id)
      }));
      
      await db.techyshop_prodotto_attributo.bulkPut(normalized);
      
      log(`Sincronizzate ${normalized.length} associazioni attributi per prodotto ${prodotto_id}.`, 'INFO');
      return { success: true };
    }
    return { success: false, message: result.text };
  } catch (error) {
    log("Errore syncProductVariantAttributes:", 'ERROR', error);
    return { success: false, message: error.message };
  }
};

/**
 * Rimuove l'associazione tra un prodotto e una categoria.
 */
export const deleteProductCategory = async (prodotto_id, categoria_id) => {
  try {
    const response = await fetch('https://deadapi.techysumo.it/techyshop/delProdottoCategoria', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': process.env.REACT_APP_API_KEY,
      },
      body: JSON.stringify({ prodotto_id, categoria_id })
    });
    const result = await response.json();
    if (result.result) {
      await db.techyshop_prodotto_categoria.where({ prodotto_id, categoria_id }).delete();
      return { success: true, message: "Categoria rimossa!" };
    }
    throw new Error(result.text || "Errore deleteProductCategory");
  } catch (error) {
    log("Errore deleteProductCategory:", 'ERROR', error);
    return { success: false, message: error.message };
  }
};

/**
 * Aggiunge una nuova variante a un prodotto.
 */
export const addProdottoVariante = async (prodotto_id) => {
  try {
    const response = await fetch('https://deadapi.techysumo.it/techyshop/addProdottoVariante', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': process.env.REACT_APP_API_KEY,
      },
      body: JSON.stringify({ prodotto_id })
    });
    const result = await response.json();
    if (result.result) {
      // Come da specifica, l'id è dentro result.details.id
      const newId = result.details.id || result.details;
      return { success: true, variante_id: parseInt(newId) };
    }
    throw new Error(result.text || "Errore addProdottoVariante");
  } catch (error) {
    log("Errore addProdottoVariante:", 'ERROR', error);
    return { success: false, message: error.message };
  }
};

/**
 * Associa un attributo e un valore a una variante di un prodotto.
 */
export const addProdottoAttributi = async (prodotto_id, prodotto_variante_id, attributo_id, attributo_valore_id) => {
  try {
    const response = await fetch('https://deadapi.techysumo.it/techyshop/addProdottoAttributi', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': process.env.REACT_APP_API_KEY,
      },
      body: JSON.stringify({ prodotto_id, prodotto_variante_id, attributo_id, attributo_valore_id })
    });
    const result = await response.json();
    if (result.result) {
      return { success: true };
    }
    throw new Error(result.text || "Errore addProdottoAttributi");
  } catch (error) {
    log("Errore addProdottoAttributi:", 'ERROR', error);
    return { success: false, message: error.message };
  }
};

/**
 * Aggiunge un nuovo prodotto nel sistema.
 * @param {Object} productData - I dati del nuovo prodotto.
 * @returns {Promise<{success: boolean, message: string, product_id?: number}>}
 */
export const addProdotto = async (productData) => {
  try {
    log("Creazione nuovo prodotto...", 'INFO');
    const response = await fetch('https://deadapi.techysumo.it/techyshop/addProdotto', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': process.env.REACT_APP_API_KEY,
      },
      body: JSON.stringify(productData)
    });
    const result = await response.json();
    if (result.result) {
      const newId = parseInt(result.details.id || result.details);
      log(`Prodotto creato con successo. ID: ${newId}`, 'INFO');
      return { success: true, message: "Prodotto creato con successo!", product_id: newId };
    }
    throw new Error(result.text || "Errore durante la creazione del prodotto.");
  } catch (error) {
    log("Errore addProdotto:", 'ERROR', error);
    return { success: false, message: error.message };
  }
};

/**
 * Edits a product via the remote API.
 * @param {Object} product - The product object to update.
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const editTechyShopProduct = async (product) => {
  try {
    const { id, ...otherData } = product;

    // Transform updated fields into the required [{field, value}, ...] format
    const values = Object.entries(otherData).map(([field, value]) => ({
      field: field,
      value: value
    }));

    const filters = [{ field: 'id', value: id }];

    log(`Aggiornamento prodotto ID ${id}...`, 'INFO');

    const response = await fetch('https://deadapi.techysumo.it/techyshop/editProdotto', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': process.env.REACT_APP_API_KEY,
      },
      body: JSON.stringify({ values, filters })
    });

    const result = await response.json();

    if (result.result) {
      // Update local Dexie record with partial or full data
      await db.techyshop_prodotti.update(id, otherData);
      log(`Prodotto ID ${id} aggiornato con successo.`, 'INFO');
      return { success: true, message: "Prodotto aggiornato correttamente!" };
    } else {
      throw new Error(result.text || "Errore durante l'aggiornamento del prodotto.");
    }
  } catch (error) {
    log("Errore durante l'aggiornamento del prodotto:", 'ERROR', error);
    return { success: false, message: `Errore: ${error.message}` };
  }
};

/**
 * Edits a product variant via the remote API.
 * @param {Object} variant - The variant object to update (can be partial, must include id).
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const editProdottoVariante = async (variant) => {
  try {
    const { id, ...otherData } = variant;
    
    // Transform updated fields into the required [{field, value}, ...] format
    const values = Object.entries(otherData).map(([field, value]) => ({
      field: field,
      value: value
    }));

    const filters = [{ field: 'id', value: id }];

    log(`Aggiornamento variante ID ${id}...`, 'INFO');

    const response = await fetch('https://deadapi.techysumo.it/techyshop/editProdottoVariante', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': process.env.REACT_APP_API_KEY,
      },
      body: JSON.stringify({ values, filters })
    });
    
    const result = await response.json();
    
    if (result.result) {
      // Update local Dexie record with partial or full data
      await db.techyshop_prodotto_varianti.update(id, otherData);
      log(`Variante ID ${id} aggiornata con successo.`, 'INFO');
      return { success: true, message: "Variante aggiornata correttamente!" };
    } else {
      throw new Error(result.text || "Errore durante l'aggiornamento della variante.");
    }
  } catch (error) {
    log("Errore durante l'aggiornamento della variante:", 'ERROR', error);
    return { success: false, message: `Errore: ${error.message}` };
  }
};

/**
 * Deletes a product variant via the remote API.
 * @param {number} id - The ID of the variant to delete.
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const deleteProdottoVariante = async (id) => {
  try {
    log(`Eliminazione variante ID ${id} sul server...`, 'INFO');
    const response = await fetch('https://deadapi.techysumo.it/techyshop/delProdottoVariante', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': process.env.REACT_APP_API_KEY,
      },
      body: JSON.stringify({ id })
    });
    const result = await response.json();
    if (result.result) {
      return { success: true, message: "Variante eliminata con successo!" };
    }
    throw new Error(result.text || "Errore durante l'eliminazione della variante sul server.");
  } catch (error) {
    log("Errore deleteProdottoVariante:", 'ERROR', error);
    return { success: false, message: error.message };
  }
};

/**
 * Genera le combinazioni di varianti per un prodotto in base agli attributi e valori scelti.
 * @param {number} prodotto_id - ID del prodotto.
 * @param {Array<number>} attributi - Lista ID attributi.
 * @param {Object} valori - Mappa { attributo_id: [valore_id, ...] }.
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const generaCombinazioneProdottoAttributi = async (prodotto_id, attributi, valori) => {
  try {
    log(`Generazione combinazioni per prodotto ID ${prodotto_id}...`, 'INFO');
    const response = await fetch('https://deadapi.techysumo.it/techyshop/generaCombinazioneProdottoAttributi', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': process.env.REACT_APP_API_KEY,
      },
      body: JSON.stringify({ prodotto_id, attributi, valori })
    });
    const result = await response.json();
    if (result.result) {
      return { success: true, message: result.text || "Combinazioni generate con successo!" };
    }
    throw new Error(result.text || "Errore durante la generazione delle combinazioni.");
  } catch (error) {
    log("Errore generaCombinazioneProdottoAttributi:", 'ERROR', error);
    return { success: false, message: error.message };
  }
};

